import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { EMPTY, Subject, catchError, firstValueFrom, switchMap, tap } from 'rxjs';
import { AUTHENTICATION_CODE_KEY } from '../authentication-page/authentication-page.component';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import {
  TraceArchiveApiService,
  TraceArchiveProtocol,
  TraceArchiveSummary
} from '../../api/trace-archive-api.service';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const SHORT_MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
const MAX_DELETE_BATCHES = 20000;
const PARIS: Intl.DateTimeFormatOptions = { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Paris' };

type WritableFile = WritableStream<Uint8Array> & {
  abort(): Promise<void>;
};

interface FileSaveHandle {
  createWritable(): Promise<WritableFile>;
}

type SavePicker = (options: {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}) => Promise<FileSaveHandle>;

@Component({
  selector: 'app-trace-cleanup-page',
  templateUrl: './trace-cleanup-page.component.html',
  styleUrls: ['./trace-cleanup-page.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule
  ]
})
export class TraceCleanupPageComponent implements OnInit {
  private readonly api = inject(TraceArchiveApiService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly load$ = new Subject<void>();

  readonly months = SHORT_MONTHS;
  readonly monthIndexes = SHORT_MONTHS.map((_, index) => index + 1);

  protocol: TraceArchiveProtocol = 'OCPP';
  year: number;
  month: number;
  readonly currentYear: number;
  readonly currentMonth: number;

  summary: TraceArchiveSummary | null = null;
  loading = false;
  downloading = false;
  deleting = false;
  deleted = 0;
  error = '';
  private readonly downloadedArchives = new Set<string>();
  private destroyed = false;

  constructor() {
    const previous = new Date();
    previous.setDate(1);
    previous.setMonth(previous.getMonth() - 1);
    this.year = previous.getFullYear();
    this.month = previous.getMonth() + 1;
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth() + 1;
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });
  }

  ngOnInit(): void {
    this.load$.pipe(
      tap(() => {
        this.loading = true;
        this.error = '';
        this.summary = null;
      }),
      switchMap(() => this.api.summary(this.protocol, this.monthKey).pipe(
        catchError((error: unknown) => {
          this.loading = false;
          this.error = this.errorMessage(error);
          return EMPTY;
        })
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(summary => {
      this.summary = summary;
      this.loading = false;
    });
    this.load$.next();
  }

  get monthKey(): string {
    return `${this.year}-${String(this.month).padStart(2, '0')}`;
  }

  get archiveKey(): string {
    return `${this.protocol}:${this.monthKey}`;
  }

  get downloaded(): boolean {
    return this.downloadedArchives.has(this.archiveKey);
  }

  get monthTitle(): string {
    return `${MONTHS[this.month - 1]} ${this.year}`;
  }

  get busy(): boolean {
    return this.downloading || this.deleting;
  }

  get canDownload(): boolean {
    return !!this.summary && this.summary.count > 0 && !this.busy && !this.loading;
  }

  get canDelete(): boolean {
    return this.canDownload && this.downloaded;
  }

  get isCurrentMonth(): boolean {
    return this.year === this.currentYear && this.month === this.currentMonth;
  }

  get countLabel(): string {
    const count = this.summary?.count ?? 0;
    const formatted = count.toLocaleString('fr-FR');
    return `${formatted} trace${count > 1 ? 's' : ''}`;
  }

  get sizeLabel(): string {
    return formatBytes(this.summary?.estimatedBytes ?? 0);
  }

  get periodLabel(): string {
    if (!this.summary) {
      return '';
    }
    const format = new Intl.DateTimeFormat('fr-FR', PARIS);
    return `Du ${format.format(new Date(this.summary.from))} au ${format.format(new Date(this.summary.to))}`;
  }

  get deleteProgress(): number {
    const total = this.summary?.count ?? 0;
    if (total <= 0) {
      return 0;
    }
    return Math.min(100, (this.deleted / total) * 100);
  }

  get deletedLabel(): string {
    return this.deleted.toLocaleString('fr-FR');
  }

  setProtocol(protocol: TraceArchiveProtocol): void {
    if (this.busy || protocol === this.protocol) {
      return;
    }
    this.protocol = protocol;
    this.load$.next();
  }

  shiftYear(delta: number): void {
    if (this.busy) {
      return;
    }
    const nextYear = this.year + delta;
    if (nextYear > this.currentYear) {
      return;
    }
    this.year = nextYear;
    if (this.isFuture(this.year, this.month)) {
      this.month = this.currentMonth;
    }
    this.load$.next();
  }

  selectMonth(month: number): void {
    if (this.busy || this.isFuture(this.year, month) || month === this.month) {
      return;
    }
    this.month = month;
    this.load$.next();
  }

  isFuture(year: number, month: number): boolean {
    return year > this.currentYear || (year === this.currentYear && month > this.currentMonth);
  }

  confirmDelete(): void {
    if (!this.canDelete || !this.summary) {
      return;
    }
    const count = this.summary.count.toLocaleString('fr-FR');
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Supprimer les traces',
        content: `Supprimer les ${count} traces ${this.protocol === 'OCPP' ? 'OCPP' : 'Okecharge'} de ${this.monthTitle} ? Cette action est définitive.`,
        confirmLabel: 'Supprimer',
        cancelLabel: 'Annuler'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed) {
        void this.purge();
      }
    });
  }

  async download(): Promise<void> {
    if (!this.canDownload) {
      return;
    }
    const archiveKey = this.archiveKey;
    const filename = `traces-${this.protocol.toLowerCase()}-${this.monthKey}.log`;
    let destination: WritableFile;
    try {
      const opened = await this.openDestination(filename);
      if (opened === 'cancelled') {
        return;
      }
      destination = opened;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Téléchargement impossible';
      return;
    }
    this.downloading = true;
    this.error = '';
    try {
      const key = localStorage.getItem(AUTHENTICATION_CODE_KEY);
      const response = await fetch(this.api.exportUrl(this.protocol, this.monthKey), {
        headers: key ? { 'X-API-KEY': key } : {}
      });
      if (!response.ok || !response.body) {
        await destination?.abort();
        throw new Error(await this.readError(response));
      }
      await response.body.pipeTo(destination);
      this.downloadedArchives.add(archiveKey);
      this.snackBar.open('Fichier téléchargé', 'Fermer', { duration: 3000 });
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Téléchargement impossible';
    } finally {
      this.downloading = false;
    }
  }

  private async purge(): Promise<void> {
    this.deleting = true;
    this.deleted = 0;
    this.error = '';
    const protocol = this.protocol;
    const month = this.monthKey;
    try {
      let finished = false;
      for (let batch = 0; batch < MAX_DELETE_BATCHES; batch++) {
        if (this.destroyed) {
          return;
        }
        const result = await firstValueFrom(this.api.deleteBatch(protocol, month));
        if (result.deleted <= 0) {
          finished = true;
          break;
        }
        this.deleted += result.deleted;
      }
      const deletedLabel = this.deleted.toLocaleString('fr-FR');
      const message = finished
        ? `${deletedLabel} traces supprimées`
        : `${deletedLabel} traces supprimées. Relancez la suppression pour continuer.`;
      this.snackBar.open(message, 'Fermer', { duration: 4000 });
    } catch (error) {
      this.error = this.errorMessage(error);
    } finally {
      this.deleting = false;
      if (!this.destroyed) {
        this.load$.next();
      }
    }
  }

  private async openDestination(filename: string): Promise<WritableFile | 'cancelled'> {
    const picker = (window as Window & { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
    if (!picker) {
      throw new Error('Ce navigateur ne peut pas enregistrer un gros fichier sans le charger en mémoire. Utilisez Chrome ou Edge.');
    }
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: 'Journal de traces', accept: { 'text/plain': ['.log'] } }]
      });
      return await handle.createWritable();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return 'cancelled';
      }
      throw new Error('Enregistrement impossible sans charger le fichier en mémoire.');
    }
  }

  private async readError(response: Response): Promise<string> {
    try {
      const body = await response.json() as { message?: string };
      if (body.message) {
        return body.message;
      }
    } catch {
      // The body is not JSON.
    }
    return 'Téléchargement impossible';
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { message?: string } | null;
      if (body && typeof body.message === 'string' && body.message) {
        return body.message;
      }
    }
    return 'Opération impossible';
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${Math.round(bytes).toLocaleString('fr-FR')} o`;
  }
  const units = ['Ko', 'Mo', 'Go', 'To'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} ${units[unit]}`;
}
